import { ReactNode, Ref, useRef } from "react"

import { ReactComponent as IconExport } from "@common_assets/svg/export.svg"
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { useExportNodeAsPng } from "hooks/exportImage"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IDashboardResponse } from "ts/interfaces/AdminDashboard"

import { dashboardTitleSx } from "./AdminDashboard.styles"
import { formatRange } from "./CustomRangeDialog"

// Only the columns that have content, so an empty one doesn't cost a gap beside the title.
export const headerGrid = (hasPeriod: boolean, hasExport: boolean) => {
  const row = (...areas: (string | false)[]) =>
    `"${areas.filter(Boolean).join(" ")}"`
  const mobileColumns = hasExport ? 2 : 1
  const fullWidth = (area: string) =>
    row(...Array.from({ length: mobileColumns }, () => area))
  return {
    gridTemplateColumns: {
      mobile: hasExport ? "1fr auto" : "1fr",
      tablet: ["1fr", hasPeriod && "auto", hasExport && "auto"]
        .filter(Boolean)
        .join(" "),
    },
    gridTemplateAreas: {
      mobile: [
        row("title", hasExport && "export"),
        hasPeriod && fullWidth("period"),
        fullWidth("question"),
      ]
        .filter(Boolean)
        .join(" "),
      tablet: [
        row("title", hasPeriod && "period", hasExport && "export"),
        row("question", hasPeriod && "period", hasExport && "export"),
      ].join(" "),
    },
  }
}

export interface IDashboardSectionExport {
  // File slug, e.g. "store-items"
  section: string
  detail?: string
  disabled: boolean
}

const DashboardSection = ({
  title,
  question,
  questionGap = 0,
  icon,
  period,
  exportImage,
  titleRef,
  children,
}: {
  title: string
  // Lets focus be moved to the section.
  titleRef?: Ref<HTMLHeadingElement>
  // Decorative, beside the title
  icon?: ReactNode
  question: string
  questionGap?: number
  // The period from the section's own response
  period?: Pick<
    IDashboardResponse,
    "date_from" | "date_to" | "prior_date_from" | "prior_date_to"
  >
  exportImage?: IDashboardSectionExport
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const cardRef = useRef<HTMLElement>(null)
  const { exportPng, isExporting } = useExportNodeAsPng(
    cardRef,
    `admin-dashboard_${exportImage?.section}_${period?.date_from}_${period?.date_to}`,
  )

  const handleExport = () => {
    if (isExporting) return
    exportPng().catch(() =>
      showSnackbar({
        variant: "error",
        title: t("adminDashboard.downloadAsImageError"),
      }),
    )
  }

  const rangeLabel = period
    ? formatRange(period.date_from, period.date_to)
    : undefined
  const periodLabel =
    rangeLabel && isExporting && exportImage?.detail
      ? `${rangeLabel} · ${exportImage.detail}`
      : rangeLabel
  const priorLabel =
    period?.prior_date_from && period.prior_date_to
      ? formatRange(period.prior_date_from, period.prior_date_to)
      : undefined

  return (
    <Box
      ref={cardRef}
      component="section"
      aria-label={title}
      sx={{
        bgcolor: "mockup.neutral100",
        border: "1px solid",
        borderColor: "mockup.neutral90",
        borderRadius: "16px",
        p: { mobile: 2, tablet: 3 },
      }}
    >
      {/* Mobile: dates under the title; tablet+: dates beside the export button */}
      <Box
        sx={{
          display: "grid",
          ...headerGrid(!!periodLabel, !!exportImage),
          columnGap: 2,
          alignItems: "start",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          columnGap={1.5}
          sx={{ gridArea: "title", minWidth: 0 }}
        >
          {icon}
          <Typography
            variant="body1"
            ref={titleRef}
            component="h3"
            tabIndex={titleRef ? -1 : undefined}
            color="mockup.neutral0"
            sx={{ ...dashboardTitleSx, "&:focus": { outline: "none" } }}
          >
            {title}
          </Typography>
        </Stack>
        {periodLabel && (
          <Stack
            alignItems="flex-start"
            justifyContent="center"
            rowGap={0.25}
            sx={{
              gridArea: "period",
              minHeight: { tablet: 40 },
              mt: { mobile: 0.5, tablet: 0 },
              mb: { mobile: 1.5, tablet: 0 },
            }}
          >
            <Box component="span" sx={visuallyHiddenSx}>
              {t("adminDashboard.sectionSelectedPeriod")}
            </Box>
            <Typography
              variant="body3"
              color="mockup.neutral10"
              fontWeight={700}
            >
              {periodLabel}
            </Typography>
            {priorLabel && (
              <>
                <Box component="span" sx={visuallyHiddenSx}>
                  {t("adminDashboard.sectionComparedWith")}
                </Box>
                <Typography
                  variant="body3"
                  color="mockup.neutral30"
                  fontWeight={500}
                >
                  {priorLabel}
                </Typography>
              </>
            )}
          </Stack>
        )}
        <Typography
          variant="body2"
          color="mockup.neutral30"
          sx={{
            gridArea: "question",
            mt: questionGap,
            whiteSpace: "pre-line",
            fontWeight: 500,
            lineHeight: "128%",
            letterSpacing: "-0.07px",
          }}
        >
          {question}
        </Typography>
        {exportImage && (
          <Tooltip title={t("adminDashboard.downloadAsImage")} describeChild>
            {/* The span keeps the tooltip working while the button is disabled */}
            <Box
              component="span"
              data-export-ignore
              sx={{ gridArea: "export", flexShrink: 0 }}
            >
              <IconButton
                aria-label={t("adminDashboard.downloadSectionAsImage", {
                  section: title,
                })}
                disabled={exportImage.disabled}
                // Not `disabled` while exporting, so keyboard focus stays on it
                aria-disabled={isExporting}
                onClick={handleExport}
                sx={{ color: "mockup.neutral30", width: 40, height: 40 }}
              >
                {isExporting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Box
                    component={IconExport}
                    aria-hidden
                    sx={{ width: 24, height: 24 }}
                  />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Box>
      <Box mt={2}>{children}</Box>
    </Box>
  )
}

export default DashboardSection
